/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getFullName } from 'src/domain/user';
import useSWR, { mutate } from 'swr';
import container from 'src/container';
import Input from 'src/components/FormComponents/Input';
import AvatarInput from 'src/components/FormComponents/AvatarInput';
import Form from 'src/components/FormComponents/Form';
import { Consumer } from 'src/domain/consumer';
import {
    ArrayNotEmpty,
    IsArray,
    IsNotEmpty,
    IsString,
    Length,
} from 'class-validator';
import { Button } from 'src/components/Buttons/Button';
import { ClosedEyeIcon, EyeIcon, UserIcon } from 'src/components/Icons/icons';
import { useNotification } from 'src/components/Notification/useNotfication';
import { Section } from 'src/components/Section';
import { useCurrentConsumerContext } from '../../Home/NewHome/reducer';
import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';
import { FieldError } from 'react-hook-form';
import { LeftGroupSelect } from 'src/components/FormComponents/LeftGroupSelect';
import { useConsumers, useCreatorRolePermission } from 'src/view/hooks';
import { OrganizationRolePermission } from 'src/domain/organization';
import { getOccupiedRoomIds } from 'src/utils/roomAvailability';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';

const {
    cradle: { munduClient },
} = container;

class ConsumerEditSchema {
    @IsString({ message: () => i18n.t('validation.requiredField') })
    @Length(2, 30, {
        message: () => i18n.t('validation.lengthBetween', { min: 2, max: 30 }),
    })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    firstName: string;

    @IsString({ message: () => i18n.t('validation.requiredField') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    lastName: string;

    @IsArray()
    @ArrayNotEmpty({ message: () => i18n.t('validation.requiredField') })
    @IsString({ each: true })
    groupIds: string[];
}

export const ConsumerProfileEdit = ({ consumer }: { consumer: Consumer }) => {
    const notify = useNotification();
    const user = useSignedInUser();
    const [resetPasswordValue, setResetPasswordValue] = useState<string>();
    // The token loads separately so the profile page doesn't wait for it.
    const { data: tokenResponse, error: tokenError } = useSWR(
        consumer.token ? null : [consumer.id, 'consumer/token'],
        () => munduClient.getConsumerToken(consumer.id),
    );
    const password =
        resetPasswordValue ??
        consumer.token ??
        tokenResponse?.token ??
        undefined;
    const isPasswordLoading =
        password === undefined && !tokenResponse && !tokenError;
    const [isResettingPassword, setIsResettingPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { t } = useTranslation();
    const hasEditPermission = useCreatorRolePermission(
        OrganizationRolePermission.writeResidents,
    );
    const canEdit = !user?.supportMode && hasEditPermission;
    const { consumers } = useConsumers({
        organizationId: consumer.organizationId,
        enabled: Boolean(consumer.organizationId),
    });
    const occupiedRoomIds = useMemo(
        () =>
            getOccupiedRoomIds(consumers, {
                excludeConsumerId: consumer.id,
            }),
        [consumer.id, consumers],
    );

    const handleUpdate = async (data: ConsumerEditSchema) => {
        const selectedGroupIds = data.groupIds?.filter(Boolean) ?? [];
        try {
            await munduClient.updateConsumer(consumer.id, {
                firstName: data.firstName,
                lastName: data.lastName,
                groupIds: selectedGroupIds,
            });
            mutate([consumer.id, 'consumer/consumerId']);
            mutate('consumers');
            notify(t('notifications.profileUpdated'));
        } catch (e) {
            if (e instanceof Error) notify(e.message, true);
        }
    };

    const resetPassword = async () => {
        try {
            setIsResettingPassword(true);
            const res = await munduClient.resetConsumerPassword(consumer.id);
            setResetPasswordValue(res.password);
            notify(t('users.profile.passwordResetSuccess'));
        } catch (e) {
            if (e instanceof Error) notify(e.message, true);
        } finally {
            setIsResettingPassword(false);
        }
    };

    const passwordDisplay = showPassword
        ? password ?? t('users.profile.passwordMissing')
        : password
        ? '••••••••'
        : t('users.profile.passwordMissing');

    return (
        <div className="bg-lightPeach flex flex-col gap-8 pb-6">
            <Section title={t('users.profile.editTitle')}>
                <Form
                    key={consumer.id}
                    className="bg-lightPeach text-woodyBrown "
                    schema={ConsumerEditSchema}
                    onSubmit={handleUpdate}
                    mode="onChange"
                    defaultValues={{
                        firstName: consumer.profile.firstName,
                        lastName: consumer.profile.lastName,
                        groupIds: consumer.groupIds ?? [],
                    }}
                >
                    {({
                        register,
                        control,
                        clearErrors,
                        formState: { isValid, errors, isSubmitting },
                    }) => (
                        <>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div className="col-span-full">
                                    <AvatarInput consumer={consumer} />
                                </div>
                                <Input
                                    className="text-input"
                                    label={t('profile.labels.firstName')}
                                    error={errors.firstName}
                                    type="text"
                                    disabled={!canEdit}
                                    {...register('firstName')}
                                />
                                <Input
                                    className="text-input"
                                    label={t('profile.labels.lastName')}
                                    error={errors.lastName}
                                    type="text"
                                    disabled={!canEdit}
                                    {...register('lastName')}
                                />
                                <Input
                                    containerClassName="col-span-full"
                                    className="text-input"
                                    label={t('profile.labels.username')}
                                    type="text"
                                    value={consumer.username}
                                    disabled
                                />
                            </div>

                            <LeftGroupSelect
                                control={control}
                                name="groupIds"
                                organizationId={consumer.organizationId}
                                error={
                                    errors.groupIds as FieldError | undefined
                                }
                                clearErrors={clearErrors}
                                disabled={!canEdit}
                                excludedGroupIds={occupiedRoomIds}
                                emptyStateVariant="available"
                            />

                            {canEdit && (
                                <div className="mt-4">
                                    <Input
                                        value={
                                            isSubmitting
                                                ? t('common.loading')
                                                : t('common.save')
                                        }
                                        className="primary-btn-small"
                                        disabled={!isValid || isSubmitting}
                                        type="submit"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Form>
            </Section>
            <Section title={t('users.profile.password')}>
                <div className="flex flex-col gap-4">
                    <div>
                        {t('users.profile.passwordDescription', {
                            name: getFullName(consumer),
                        })}
                    </div>
                    <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                        <div>
                            <p className="text-xs uppercase tracking-wide text-mongooseGray">
                                {t('users.profile.passwordLabel')}
                            </p>
                            <div className="flex items-center gap-1">
                                {isPasswordLoading ? (
                                    <div
                                        role="status"
                                        className="h-8 w-32 animate-pulse rounded bg-mongooseGray bg-opacity-25"
                                    />
                                ) : (
                                    <p className="font-semibold text-2xl text-woodyBrown">
                                        {passwordDisplay}
                                    </p>
                                )}
                                <button
                                    type="button"
                                    className="relative flex h-8 w-8 items-center justify-center text-brightOrange transition hover:scale-105 disabled:cursor-not-allowed disabled:text-mongooseGray"
                                    onClick={() =>
                                        setShowPassword(prev => !prev)
                                    }
                                    disabled={!password}
                                >
                                    <span
                                        aria-hidden
                                        className="relative block h-5 w-5"
                                    >
                                        {showPassword ? (
                                            <EyeIcon />
                                        ) : (
                                            <ClosedEyeIcon />
                                        )}
                                    </span>
                                </button>
                            </div>
                        </div>
                        <Button
                            title={t('users.profile.resetButton')}
                            onClick={resetPassword}
                            disabled={isResettingPassword || !canEdit}
                        />
                    </div>
                    <div className="text-sm text-mongooseGray">
                        {t('users.profile.resetInfo', {
                            name: getFullName(consumer),
                        })}
                    </div>
                </div>
            </Section>

            {canEdit && (
                <Section title={t('users.profile.deleteTitle')}>
                    <DeleteConsumer consumer={consumer} />
                </Section>
            )}
        </div>
    );
};

const DeleteConsumer = ({
    consumer,
}: {
    consumer: Consumer;
    onDelete?: () => void;
}) => {
    const [confirmationUsername, setConfirmationUsername] = useState('');
    const [canDelete, setCanDelete] = useState(false);
    const history = useHistory();
    const notify = useNotification();
    const { dispatch } = useCurrentConsumerContext();
    const { t } = useTranslation();

    const onDelete = async () => {
        munduClient
            .deleteConsumer(consumer.id)
            .then(() => {
                notify(
                    t('users.notifications.deleted', {
                        name: getFullName(consumer),
                    }),
                );
                dispatch({ type: 'RESET' });
                mutate('consumers');
                history.push('/app/home');
            })
            .catch((error: Error) => notify(error.message, true));
    };

    useEffect(() => {
        if (confirmationUsername === consumer.username) {
            setCanDelete(true);
        } else {
            setCanDelete(false);
        }
    }, [confirmationUsername, consumer.username]);

    return (
        <>
            <div>{t('users.profile.deleteDescription')}</div>
            <div className="self-start flex items-end gap-3 flex-wrap">
                <Input
                    className="text-input"
                    type="text"
                    onChange={e =>
                        setConfirmationUsername(e.currentTarget.value)
                    }
                    label={t('users.profile.confirmLabel', {
                        value: consumer.username,
                    })}
                    leadingIcon={UserIcon}
                />
                <Button
                    title={t('users.profile.deleteButton')}
                    onClick={onDelete}
                    disabled={!canDelete}
                />
            </div>
        </>
    );
};
